/**
 * WidgetRegistry — Singleton registry for all Chanelle OS widget definitions.
 *
 * Usage:
 *   import { widgetRegistry } from './WidgetRegistry';
 *   const widget = widgetRegistry.getWidget('my_tasks');
 */

import type { WidgetDefinition } from '../core/types';

class WidgetRegistry {
  private static instance: WidgetRegistry;
  private widgets: Map<string, WidgetDefinition> = new Map();

  private constructor() {}

  static getInstance(): WidgetRegistry {
    if (!WidgetRegistry.instance) {
      WidgetRegistry.instance = new WidgetRegistry();
    }
    return WidgetRegistry.instance;
  }

  /** Register a widget definition. Throws if id already registered (prevents accidental duplication). */
  register(definition: WidgetDefinition): void {
    if (this.widgets.has(definition.id)) {
      console.warn(`[WidgetRegistry] Widget "${definition.id}" is already registered. Skipping.`);
      return;
    }
    this.widgets.set(definition.id, definition);
  }

  /** Register multiple widget definitions at once. */
  registerMany(definitions: WidgetDefinition[]): void {
    definitions.forEach((def) => this.register(def));
  }

  /** Retrieve a single widget definition by id. Returns undefined if not found. */
  getWidget(id: string): WidgetDefinition | undefined {
    return this.widgets.get(id);
  }

  /** Retrieve all registered widget definitions. */
  getAllWidgets(): WidgetDefinition[] {
    return Array.from(this.widgets.values());
  }

  /** Retrieve widgets filtered by surface (custom_object | dashboard). */
  getWidgetsBySurface(surface: 'custom_object' | 'dashboard'): WidgetDefinition[] {
    return this.getAllWidgets().filter((w) => w.surfaces.includes(surface));
  }

  /** Retrieve widgets filtered by category. */
  getWidgetsByCategory(category: string): WidgetDefinition[] {
    return this.getAllWidgets().filter((w) => w.category === category);
  }

  /** Check if a widget id is registered. */
  has(id: string): boolean {
    return this.widgets.has(id);
  }

  /** Total number of registered widgets. */
  get size(): number {
    return this.widgets.size;
  }

  /** Unregister a widget (useful in tests). */
  unregister(id: string): void {
    this.widgets.delete(id);
  }

  /** Clear all registrations (useful in tests). */
  clear(): void {
    this.widgets.clear();
  }
}

// Export the singleton instance
export const widgetRegistry = WidgetRegistry.getInstance();

// -----------------------------------------------------------------
// Auto-register all built-in widgets when this module is imported.
// Each widget module registers itself via a side-effect import below.
// We use lazy dynamic imports so webpack can code-split them.
// -----------------------------------------------------------------

export async function initializeWidgetRegistry(): Promise<void> {
  const modules = await Promise.all([
    import('../widgets/MyTasks'),
    import('../widgets/Inbox'),
    import('../widgets/BoardPulse'),
    import('../widgets/VaultCapture'),
    import('../widgets/AnalyticsSnapshot'),
    import('../widgets/QuickActions'),
    import('../widgets/RecentNotes'),
    import('../widgets/BoardHealth'),
    import('../widgets/GoogleCalendar'),
    import('../widgets/FocusSuggestion'),
    import('../widgets/Alerts'),
    import('../widgets/Meetings'),
    import('../widgets/Topics'),
  ]);

  modules.forEach((mod) => {
    if (mod.definition) {
      widgetRegistry.register(mod.definition);
    }
  });

  console.info(`[WidgetRegistry] Initialised with ${widgetRegistry.size} widgets.`);
}

/** Return the singleton WidgetRegistry instance. */
export function getWidgetRegistry() {
  return widgetRegistry;
}
